import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Trello, FileText, MapPin, Globe, CheckCircle2, Check,
  ArrowRight, Sparkles, LayoutGrid, Calendar, ShieldCheck, Zap,
  Navigation, MousePointer2, UserCheck, Trophy, Star, Award, Smartphone,
  TrendingUp, Target
} from 'lucide-react';
import { PLAN_LIMITS } from '../types';

interface LandingPageProps {
  onLoginClick: () => void;
  onNavigate: (page: string) => void;
  onOpenSupport: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick, onNavigate, onOpenSupport }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const features = [
    {
      title: 'CRM / Contatos',
      desc: 'Gestão completa da sua base de cidadãos com segmentação inteligente e histórico.',
      icon: Users,
      color: 'bg-parliament-500'
    },
    {
      title: 'Kanban de Tarefas',
      desc: 'Fluxo operacional organizado para sua equipe não perder nenhum prazo importante.',
      icon: Trello,
      color: 'bg-indigo-500'
    },
    {
      title: 'Mapa Estratégico',
      desc: 'Visualize sua influência no território através de geolocalização precisa.',
      icon: MapPin,
      color: 'bg-emerald-500'
    },
    {
      title: 'Gestão de Documentos',
      desc: 'Protocolos, ofícios e projetos legislativos centralizados e rastreáveis.',
      icon: FileText,
      color: 'bg-amber-500'
    }
  ];

  const plans = [
    {
      id: 'essential',
      label: 'Essencial',
      price: PLAN_LIMITS.essential.installmentYearly,
      total: PLAN_LIMITS.essential.priceYearly,
      features: [`Até ${PLAN_LIMITS.essential.contacts.toLocaleString()} Contatos`, 'Até 3 assessores', '5GB Armazenamento', 'Gestão Completa', 'Mapa Estratégico']
    },
    {
      id: 'advanced',
      label: 'Avançado',
      price: PLAN_LIMITS.advanced.installmentYearly,
      total: PLAN_LIMITS.advanced.priceYearly,
      recommended: true,
      features: [`Até ${PLAN_LIMITS.advanced.contacts.toLocaleString()} Contatos`, 'Sem limite de assessores', 'Inclua lideranças externas', '50GB Armazenamento', 'Gestão Completa', 'Mapa Estratégico']
    },
    {
      id: 'pro',
      label: 'Pro',
      price: PLAN_LIMITS.pro.installmentYearly,
      total: PLAN_LIMITS.pro.priceYearly,
      features: ['Contatos Ilimitados', 'Equipe Ilimitada', '100GB Armazenamento', 'Gestão Completa', 'Mapa Estratégico']
    }
  ];

  return (
    <div className="bg-white dark:bg-navy-950 font-sans selection:bg-parliament-200 overflow-x-hidden">
      {/* Header */}
      <nav className="fixed top-0 w-full z-[100] bg-white/80 dark:bg-navy-950/80 backdrop-blur-md border-b border-neutral-100 dark:border-navy-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-navy-900 dark:bg-parliament-600 p-2 rounded-xl text-white shadow-lg shadow-parliament-500/10">
              <Globe size={24} />
            </div>
            <span className="font-black text-xl tracking-tighter uppercase dark:text-white">Gabinete Digital</span>
          </div>
          <button 
            onClick={onLoginClick}
            className="px-6 py-2.5 bg-navy-950 dark:bg-white text-white dark:text-navy-950 rounded-xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-xl shadow-black/5"
          >
            Conectar-se
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 px-6 overflow-hidden min-h-screen flex items-center">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-navy-900 via-navy-950 to-black pointer-events-none"></div>
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-parliament-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-emerald-600/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-parliament-500/10 border border-parliament-500/30 text-parliament-400 text-[10px] font-black uppercase tracking-widest mb-8">
              <Sparkles size={14} /> Transformação Digital Parlamentar
            </span>
            <h1 className="text-5xl md:text-8xl font-black text-white tracking-tighter leading-[1.1] mb-8">
              Sua ferramenta de <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-parliament-400 to-indigo-400">gestão eficiente.</span>
            </h1>
            <p className="text-slate-400 text-lg md:text-xl font-medium max-w-2xl mx-auto mb-12">
              Tudo que você precisa num lugar só. Uma plataforma robusta desenvolvida para otimizar a presença territorial e legislativa do seu mandato.
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <button 
                onClick={onLoginClick}
                className="w-full md:auto px-12 py-6 bg-parliament-600 text-white rounded-[2rem] font-black text-sm uppercase tracking-[0.25em] shadow-[0_20px_50px_-10px_rgba(0,132,255,0.4)] hover:bg-parliament-500 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-4 group"
              >
                Começar Agora <ArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-32 px-6 bg-neutral-50 dark:bg-navy-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-5xl font-black text-navy-950 dark:text-white tracking-tighter mb-4 uppercase">
              Potencializamos seu Mandato
            </h2>
            <div className="h-2 w-24 bg-parliament-600 mx-auto rounded-full"></div>
          </div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feat, idx) => (
              <motion.div 
                key={idx}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-white dark:bg-navy-900 p-10 rounded-[3rem] border border-neutral-100 dark:border-navy-800 shadow-xl shadow-black/5 flex flex-col items-center text-center group"
              >
                <div className={`${feat.color} p-5 rounded-3xl text-white mb-8 shadow-lg group-hover:scale-110 transition-transform`}>
                  <feat.icon size={32} />
                </div>
                <h3 className="text-xl font-black text-navy-950 dark:text-white mb-4 uppercase tracking-tighter">{feat.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm font-medium leading-relaxed">{feat.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Kanban Animation Section */}
      <section className="py-32 px-6 relative overflow-hidden border-b border-neutral-100 dark:border-navy-900">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-black text-navy-950 dark:text-white tracking-tighter mb-8 leading-tight">
              Acompanhamento <br /> em Tempo Real.
            </h2>
            <div className="space-y-8">
              {[
                { icon: LayoutGrid, title: 'Interface Kanban', text: 'Arraste e solte tarefas, gerencie responsabilidades.' },
                { icon: Calendar, title: 'Calendário Integrado', text: 'Visão completa de agendas e prazos fatais.' },
                { icon: ShieldCheck, title: 'Segurança de Dados', text: 'Toda sua base blindada com tecnologia Supabase.' },
                { icon: Zap, title: 'Alta Performance', text: 'Sistema otimizado para navegadores e dispositivos móveis.' },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6">
                  <div className="bg-parliament-50 dark:bg-navy-800 p-4 rounded-2xl text-parliament-600 shrink-0">
                    <item.icon size={24} />
                  </div>
                  <div>
                    <h4 className="font-black text-navy-900 dark:text-white uppercase text-sm mb-1 tracking-widest">{item.title}</h4>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div 
             initial={{ opacity: 0, scale: 0.8 }}
             whileInView={{ opacity: 1, scale: 1 }}
             viewport={{ once: true }}
             className="relative"
          >
            <div className="bg-navy-900 rounded-[3rem] p-8 shadow-2xl border border-navy-800 overflow-hidden">
               <div className="grid grid-cols-3 gap-4 h-[400px]">
                  {['A Fazer', 'Execução', 'Concluído'].map((label, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl p-4 flex flex-col gap-3 relative">
                       <p className="text-[8px] font-black uppercase text-slate-500 tracking-widest mb-2">{label}</p>
                       {i === 0 && (
                          <>
                            <div className="bg-white/5 border border-white/10 p-3 rounded-xl space-y-2">
                               <div className="h-1.5 w-1/2 bg-white/20 rounded-full"></div>
                               <div className="h-1 w-full bg-white/10 rounded-full"></div>
                            </div>
                             <div className="bg-white/5 border border-white/10 p-3 rounded-xl space-y-2 opacity-50">
                               <div className="h-1.5 w-3/4 bg-white/20 rounded-full"></div>
                               <div className="h-1 w-full bg-white/10 rounded-full"></div>
                            </div>
                          </>
                       )}
                       {i === 2 && (
                          <div className="bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-xl space-y-2">
                             <div className="h-1.5 w-2/3 bg-emerald-400/30 rounded-full"></div>
                             <div className="flex justify-between items-center">
                                <div className="h-1 w-1/3 bg-emerald-400/20 rounded-full"></div>
                                <Check size={10} className="text-emerald-400" />
                             </div>
                          </div>
                       )}
                       {i === 0 && (
                          <motion.div 
                             animate={{ 
                                x: [0, 160, 160, 0],
                                rotate: [0, 2, -2, 0],
                                scale: [1, 1.05, 1.05, 1],
                             }}
                             transition={{ 
                                duration: 4, 
                                repeat: Infinity, 
                                repeatDelay: 1,
                                times: [0, 0.4, 0.7, 1],
                                ease: "easeInOut"
                             }}
                             className="bg-white dark:bg-navy-800 p-4 rounded-xl border border-parliament-500/30 shadow-lg relative z-20"
                          >
                             <div className="flex justify-between items-start mb-3">
                                <div className="h-2 w-16 bg-parliament-500/40 rounded-full"></div>
                                <div className="w-4 h-4 rounded-full bg-parliament-600/20 flex items-center justify-center">
                                   <div className="w-1.5 h-1.5 rounded-full bg-parliament-50"></div>
                                </div>
                             </div>
                             <p className="text-[10px] font-black text-navy-950 dark:text-white uppercase tracking-tighter mb-2">Requerimento nº 127</p>
                             <div className="space-y-1.5">
                                <div className="h-1 w-full bg-slate-200 dark:bg-white/10 rounded-full"></div>
                                <div className="h-1 w-3/4 bg-slate-200 dark:bg-white/10 rounded-full"></div>
                             </div>
                          </motion.div>
                       )}
                    </div>
                  ))}
               </div>
            </div>
            <div className="absolute -bottom-10 -left-10 bg-emerald-500 text-white p-6 rounded-[2rem] shadow-2xl flex flex-col items-center animate-bounce z-30">
              <CheckCircle2 size={32} />
              <span className="font-black text-[10px] uppercase mt-2 tracking-widest">Sincronizado</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Strategic Map Simulation Section */}
      <section className="py-32 px-6 bg-neutral-50 dark:bg-navy-900/30 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="order-2 lg:order-1 relative"
          >
            <div className="bg-navy-950 rounded-[4rem] h-[500px] w-full border-4 border-white dark:border-navy-800 shadow-2xl relative overflow-hidden">
               <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
               
               <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 400">
                  <path d="M50,100 Q150,50 250,150 T350,250 T200,350 T50,250 Z" fill="url(#grad)" />
                  <defs>
                    <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#0069ff" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                  </defs>
               </svg>

               {[
                 { t: '15%', l: '25%', d: 0 },
                 { t: '65%', l: '45%', d: 0.5 },
                 { t: '40%', l: '75%', d: 1.2 },
                 { t: '80%', l: '15%', d: 1.8 }
               ].map((pos, i) => (
                 <div key={i} className="absolute" style={{ top: pos.t, left: pos.l }}>
                    <div className="relative">
                       <div className="absolute -inset-4 bg-emerald-500/20 rounded-full animate-ping"></div>
                       <motion.div 
                         initial={{ scale: 0 }}
                         animate={{ scale: 1 }}
                         transition={{ delay: pos.d, duration: 0.5 }}
                         className="bg-emerald-500 p-2 rounded-full text-white shadow-lg relative z-10"
                       >
                         <MapPin size={16} />
                       </motion.div>
                    </div>
                 </div>
               ))}

               <motion.div 
                 initial={{ x: 50, opacity: 0 }}
                 whileInView={{ x: 0, opacity: 1 }}
                 transition={{ delay: 1, duration: 0.8 }}
                 className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 bg-white dark:bg-navy-900 rounded-3xl p-6 shadow-2xl border border-parliament-100 dark:border-navy-700 z-40"
               >
                  <div className="flex items-center gap-3 mb-4">
                     <div className="w-10 h-10 bg-parliament-600 rounded-xl flex items-center justify-center text-white font-black">R</div>
                     <div>
                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Apoiador Local</p>
                        <h4 className="text-xs font-black dark:text-white uppercase">Rodrigo Silva</h4>
                     </div>
                  </div>
                  <div className="space-y-2">
                     <div className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Cidade:</span> <span className="dark:text-white">São Paulo</span></div>
                     <div className="flex justify-between text-[10px] font-bold"><span className="text-slate-400">Nível:</span> <span className="text-emerald-500 uppercase">Apoiador</span></div>
                  </div>
               </motion.div>

               <motion.div 
                  animate={{ y: [0, 500, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-parliament-400 to-transparent z-20 shadow-[0_0_15px_rgba(0,105,255,0.5)]"
               />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="order-1 lg:order-2"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 text-[10px] font-black uppercase tracking-widest mb-6">
              <Navigation size={14} /> Inteligência Territorial
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-navy-950 dark:text-white tracking-tighter mb-8 leading-tight">
              Visão completa <br /> da sua influência.
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-lg font-medium mb-10 leading-relaxed">
              Identifique apoiadores, demandas regionais e áreas de atuação com um mapa estratégico integrado ao CRM. Transforme dados em presença política real.
            </p>
            <div className="space-y-4">
              {[
                { icon: UserCheck, title: 'Clusterização de Dados', text: 'Agrupe eleitores por bairro ou nível de apoio.' },
                { icon: MousePointer2, title: 'Interação Direta', text: 'Abra perfis e atendimentos diretamente do mapa.' },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 p-6 bg-white dark:bg-navy-900 rounded-[2rem] border border-neutral-100 dark:border-navy-800 shadow-sm">
                   <div className="p-3 bg-emerald-50 dark:bg-navy-800 text-emerald-600 rounded-2xl shrink-0"><item.icon size={24} /></div>
                   <div>
                      <h4 className="font-black text-navy-900 dark:text-white uppercase text-xs mb-1 tracking-widest">{item.title}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">{item.text}</p>
                   </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leadership Profile & Gamification Section */}
      <section className="py-32 px-6 relative border-t border-neutral-100 dark:border-navy-900">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-600 text-[10px] font-black uppercase tracking-widest mb-6">
              <Trophy size={14} /> Engajamento e Meritocracia
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-navy-950 dark:text-white tracking-tighter mb-8 leading-tight">
              Gamificação para <br /> Lideranças Externas.
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-lg font-medium mb-12 leading-relaxed">
              Dê voz e propósito à sua base externa. Com perfis exclusivos para lideranças, seus apoiadores pontuam ao cadastrar cidadãos e trazer demandas, gerando um ranking de produtividade saudável.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { icon: Smartphone, title: 'Acesso Mobile', text: 'Interface simplificada para uso em campo pelo WhatsApp.' },
                { icon: TrendingUp, title: 'Ranking Ativo', text: 'Lideranças acompanham seu progresso e metas.' },
                { icon: Award, title: 'Recompensas', text: 'Reconheça seus melhores captadores de forma automatizada.' },
                { icon: Target, title: 'Metas Regionais', text: 'Direcione esforços para bairros específicos com desafios.' },
              ].map((item, idx) => (
                <div key={idx} className="p-6 bg-neutral-50 dark:bg-navy-900 rounded-[2.5rem] border border-transparent hover:border-amber-200 transition-all group">
                   <div className="p-3 bg-white dark:bg-navy-800 text-amber-500 rounded-2xl w-fit mb-4 shadow-sm group-hover:scale-110 transition-transform"><item.icon size={24} /></div>
                   <h4 className="font-black text-navy-900 dark:text-white uppercase text-[10px] mb-2 tracking-widest">{item.title}</h4>
                   <p className="text-slate-500 dark:text-slate-400 text-xs font-medium leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div 
             initial={{ opacity: 0, scale: 0.8 }}
             whileInView={{ opacity: 1, scale: 1 }}
             viewport={{ once: true }}
             className="relative"
          >
            {/* Gamification Mockup Ranking */}
            <div className="bg-navy-900 rounded-[4rem] p-10 shadow-2xl border border-navy-800 relative">
               <div className="flex items-center gap-4 mb-10">
                  <div className="p-4 bg-amber-500 text-white rounded-3xl shadow-lg"><Trophy size={32} /></div>
                  <div>
                     <h3 className="text-2xl font-black text-white uppercase tracking-tighter">Ranking de Campo</h3>
                     <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Semana de Atuação Territorial</p>
                  </div>
               </div>

               <div className="space-y-4">
                  {[
                    { name: 'Ricardo Martins', pts: 2450, color: 'bg-yellow-400', rank: '1º' },
                    { name: 'Sueli Oliveira', pts: 1820, color: 'bg-slate-300', rank: '2º' },
                    { name: 'Marcos Vinicius', pts: 1560, color: 'bg-orange-400', rank: '3º' },
                  ].map((user, i) => (
                    <motion.div 
                      key={i} 
                      whileHover={{ x: 10 }}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/5 hover:bg-white/10 transition-all"
                    >
                       <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl ${user.color} text-navy-950 flex items-center justify-center font-black text-sm shadow-md`}>{user.rank}</div>
                          <div>
                             <p className="text-sm font-bold text-white">{user.name}</p>
                             <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Liderança Regional</p>
                          </div>
                       </div>
                       <div className="text-right">
                          <p className="text-lg font-black text-amber-500">{user.pts}</p>
                          <p className="text-[8px] font-black text-slate-500 uppercase">Pontos</p>
                       </div>
                    </motion.div>
                  ))}
               </div>

               {/* Reward Card Floating */}
               <motion.div 
                 animate={{ y: [0, -15, 0] }}
                 transition={{ duration: 4, repeat: Infinity }}
                 className="absolute -top-10 -right-10 bg-white dark:bg-navy-800 p-6 rounded-[2.5rem] shadow-2xl border border-amber-100 dark:border-navy-700 z-20 text-center"
               >
                  <div className="bg-amber-100 p-3 rounded-full text-amber-600 inline-block mb-3"><Star size={24} fill="currentColor" /></div>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Nova Medalha</p>
                  <h5 className="text-xs font-black text-navy-900 dark:text-white uppercase">Mestre Territorial</h5>
               </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-32 px-6 bg-navy-950 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_bottom,_var(--tw-gradient-stops))] from-parliament-900/20 via-transparent to-transparent pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-24">
            <h2 className="text-5xl font-black text-white tracking-tighter mb-6 uppercase">Invista no seu Legado</h2>
            <p className="text-parliament-400 font-black text-xs uppercase tracking-[0.3em]">Escolha o plano ideal para seu gabinete</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {plans.map(plan => (
              <motion.div 
                key={plan.id}
                whileHover={{ y: -10 }}
                className={`relative p-10 rounded-[3rem] border-2 flex flex-col ${plan.recommended ? 'border-parliament-500 bg-navy-900 shadow-2xl scale-105 z-10' : 'border-white/10 bg-white/5 backdrop-blur-sm'}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-parliament-600 to-indigo-600 text-white px-8 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl flex items-center gap-2">
                    <Sparkles size={14} /> RECOMENDADO
                  </div>
                )}
                
                <h3 className="text-2xl font-black text-white mb-8 tracking-tight uppercase">{plan.label}</h3>
                
                <div className="mb-10">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-slate-400">R$</span>
                    <span className="text-6xl font-black text-white leading-none">{plan.price.toFixed(2).replace('.', ',')}</span>
                    <span className="text-slate-500 font-bold">/mês</span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-4">
                    Pagamento Único Anual: {plan.total.toFixed(0)}
                  </p>
                  <p className="text-[10px] text-emerald-400 font-black uppercase tracking-widest mt-1 flex items-center gap-2">
                    <Check size={12} /> Até 12x sem juros no cartão
                  </p>
                </div>

                <ul className="space-y-5 mb-12 flex-1 border-t border-white/10 pt-8">
                  {plan.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-4 text-sm font-bold text-slate-300">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20">
                        <Check size={12} strokeWidth={4} />
                      </div>
                      {feat}
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={onLoginClick}
                  className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${plan.recommended ? 'bg-white text-navy-950 shadow-xl' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
                >
                  Selecionar Plano
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-32 px-6">
        <div className="max-w-5xl mx-auto bg-gradient-to-r from-parliament-600 to-indigo-600 rounded-[4rem] p-12 md:p-24 text-center text-white relative overflow-hidden shadow-2xl">
           <div className="absolute top-0 right-0 p-8 opacity-10"><Globe size={300} /></div>
           <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8 leading-tight">
             Pronto para profissionalizar sua gestão?
           </h2>
           <button 
             onClick={onLoginClick}
             className="px-16 py-6 bg-white text-parliament-700 rounded-[2rem] font-black text-sm uppercase tracking-[0.25em] shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-4 mx-auto group"
           >
             CONECTAR-SE AGORA <ArrowRight className="group-hover:translate-x-2 transition-transform" />
           </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-navy-900 bg-navy-950">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-3">
             <div className="bg-navy-800 p-2 rounded-lg text-white">
                <Globe size={20} />
             </div>
             <div className="flex flex-col gap-1">
               <span className="font-black text-sm tracking-tight uppercase text-white">Gabinete Digital &copy; 2026</span>
               <div className="flex flex-wrap items-center gap-1.5 text-sm font-serif text-slate-400">
                 <span>para gestões eficientes, um produto de</span>
                 <a href="https://dialetike.org/" target="_blank" rel="noopener noreferrer">
                   <img src="/dialetike-logo.svg" alt="dialetike" className="h-5 w-auto" />
                 </a>
               </div>
             </div>
          </div>
          <div className="flex gap-8">
             <button onClick={() => onNavigate('terms')} className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-parliament-400 transition-colors">Termos</button>
             <button onClick={() => onNavigate('privacy')} className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-parliament-400 transition-colors">Privacidade</button>
             <button onClick={onOpenSupport} className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-parliament-400 transition-colors">Suporte</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;